import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Progress,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  chakra,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useKydEvent } from "../../hooks/useKydEvent";
import { useDialogWithOptions } from "../../../../Common/Dialog";
import { useLoading } from "../../../hooks/form";
import actions from "../../../../actions";
import CurrencyInput from "react-currency-input-field";
import { useEffect, useState, useCallback } from "react";
import { FaCheck, FaEdit, FaPlus } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import { badgeForStatus } from "../../../Events/EventsTable";
import AppStoreBadge from "../../../../download-appstore.svg";
import LinkOrganizationDialog, {
  LinkOrganizationDialogContext,
  withLinkOrganizationDialog,
} from "./LinkOrganizationDialog";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import { withConfirmDialog } from "../../ConfirmDialog";
import LinkedOrganizationsTable from "./LinkedOrganizationsTable";
import { DangerZone } from "./DangerZone";
import { getEventStatusText } from "../../utils/eventStatus";
import { useOrganizationSettings } from "../../../hooks/useOrganizationSettings";
import RebateForm from "../../../Settings/RebateForm";
import { calculatePreviewData as calculatePreviewDataFromAPI } from "../../../utils/feeCalculations";

const ColoredText = chakra.span;

const SettingsNavSection = () => {
  const { event_id } = useParams();
  const { kydEvent, refetch, loading: isEventLoading } = useKydEvent(event_id);
  const { organization: orgData } = useOrganizationSettings();
  const { open, close } = useDialogWithOptions();
  const [breakeven, setBreakeven] = useState("");
  const [isEditingBreakeven, setIsEditingBreakeven] = useState(false);
  const [editingLinkedOrganization, setEditingLinkedOrganization] =
    useState<KYDOrganizationEventLink | null>(null);
  const [loading, setLoading] = useLoading({
    settings: false,
    listing: false,
    link_organization: false,
    rebate: false,
  });
  const toast = useToast();
  const dialog = useDialogWithOptions();
  const navigate = useNavigate();
  const {
    open: openLinkOrganizationDialog,
    close: closeLinkOrganizationDialog,
  } = useDialogWithOptions(LinkOrganizationDialogContext);

  // Cache for fee calculations
  const [feeCache, setFeeCache] = useState<Record<number, number>>({});
  const [isCalculatingFees, setIsCalculatingFees] = useState(false);
  const rebateUiEnabled = !!orgData?.settings?.rebates_enabled;

  // Helper: Convert rebate amount from storage format to display format
  // Rebates are always stored in bips (basis points) for percentage mode
  // Divide by 100 to convert from bips (e.g., 500 bips = 5%) to percentage display value
  const convertRebateAmount = (amount: number): number => {
    return amount / 100;
  };

  // Get event rebate from fees_override
  const getEventRebate = () => {
    const rebateFee = kydEvent?.fees_override?.find(
      (fee) => fee.type === "rebate"
    );
    if (!rebateFee) return null;

    // Return null if breaks array is empty or amount is 0
    const firstBreakAmount = rebateFee.breaks?.[0]?.amount;
    if (!firstBreakAmount || firstBreakAmount === 0) return null;

    return {
      amount: convertRebateAmount(firstBreakAmount),
    };
  };

  // Get organization rebate from fees
  const getOrgRebate = () => {
    const rebateFee = orgData?.fees?.find((fee) => fee.type === "rebate");
    if (!rebateFee) return null;

    // Return null if breaks array is empty or amount is 0
    const firstBreakAmount = rebateFee.breaks?.[0]?.amount;
    if (!firstBreakAmount || firstBreakAmount === 0) return null;

    return {
      amount: convertRebateAmount(firstBreakAmount),
    };
  };

  const eventRebate = getEventRebate();
  const orgRebate = getOrgRebate();

  // Helper: Get initial rebate config from current rebates
  const getInitialRebateConfig = () => {
    const rebate = eventRebate || orgRebate;
    return {
      amount: rebate ? rebate.amount.toFixed(2) : "",
    };
  };

  const [isEditingRebate, setIsEditingRebate] = useState(false);
  const [isCreatingRebate, setIsCreatingRebate] = useState(false);
  const [rebateConfig, setRebateConfig] = useState(getInitialRebateConfig);

  useEffect(() => {
    setRebateConfig(getInitialRebateConfig());
    setIsEditingRebate(false);
    setIsCreatingRebate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kydEvent?.fees_override, orgData?.fees]);

  // Helper: Show toast notification
  const showToast = (
    title: string,
    description: string,
    status: "success" | "error" | "info" = "info"
  ) => {
    toast({
      title,
      description,
      status,
      duration: 5000,
      isClosable: true,
    });
  };

  const handleListingChange = async (listed: boolean) => {
    if (!kydEvent) {
      return;
    }

    setLoading("listing", true);
    try {
      const updatedEvent = await actions.ADMIN.updateEventSync(
        kydEvent.id,
        "listed",
        listed
      );
      showToast(
        listed ? "Event listed" : "Event unlisted",
        listed
          ? "This event will appear in your bio and partner feeds"
          : "This event is hidden from your bio and partner feeds",
        "success"
      );
      await refetch({ silent: true, event: updatedEvent });
    } catch (err) {
      showToast(
        "Unable to update event listing",
        err instanceof Error ? err.message : "Please try again.",
        "error"
      );
    } finally {
      setLoading("listing", false);
    }
  };

  // Validate rebate input
  const validateRebateInput = (): boolean => {
    if (!rebateConfig.amount || isNaN(parseFloat(rebateConfig.amount))) {
      showToast(
        "Invalid rebate",
        "Please enter a valid rebate percentage",
        "error"
      );
      return false;
    }

    const amountNum = parseFloat(rebateConfig.amount);
    if (amountNum < 0 || amountNum > 25) {
      showToast(
        "Invalid percentage",
        "Percentage must be between 0 and 25",
        "error"
      );
      return false;
    }
    return true;
  };

  const handleRebateSave = async () => {
    if (!validateRebateInput()) return;

    setLoading("rebate", true);
    const amountNum = parseFloat(rebateConfig.amount);
    // Convert percentage to bips (basis points) for storage
    // e.g., 5% -> 500 bips
    const rebateAmount = Math.round(amountNum * 100);

    try {
      const updatedEvent = await actions.ADMIN.updateEventSync(
        kydEvent.id,
        "rebate",
        {
          mode: "percentage",
          amount: rebateAmount,
        }
      );
      showToast(
        "Rebate override saved",
        "Event-level rebate override has been set",
        "success"
      );
      await refetch({ silent: true, event: updatedEvent });
      setIsEditingRebate(false);
      setIsCreatingRebate(false);
    } catch (err) {
      showToast("Error", err.message, "error");
    } finally {
      setLoading("rebate", false);
    }
  };

  const handleRevertToGlobal = async () => {
    setLoading("rebate", true);
    try {
      const updatedEvent = await actions.ADMIN.updateEventSync(
        kydEvent.id,
        "rebate",
        null
      );
      showToast(
        "Rebate reverted",
        "Event will now use organization-level rebate",
        "success"
      );
      await refetch({ silent: true, event: updatedEvent });
    } catch (err) {
      showToast("Error", err.message, "error");
    } finally {
      setLoading("rebate", false);
    }
  };

  // Helper: Format rebate display (always percentage)
  const formatRebateDisplay = (rebate: {
    mode?: "percentage" | "fixed"; // Deprecated: always percentage now
    amount: number;
  }): string => {
    return `${rebate.amount}%`;
  };

  // Helper: Calculate fees using API (async)
  const calculateFeesForForm = useCallback(
    async (faceValue: number): Promise<number> => {
      if (!event_id || !kydEvent) {
        // Fallback: return 0 if no event_id
        return 0;
      }

      // Check cache first
      if (feeCache[faceValue] !== undefined) {
        return feeCache[faceValue];
      }

      try {
        const { total_fees } = await actions.ADMIN.calculateFinalTicketPrice(
          faceValue,
          event_id
        );
        const feesInDollars = total_fees.getAmount() / 100;

        // Update cache
        setFeeCache((prev) => ({
          ...prev,
          [faceValue]: feesInDollars,
        }));

        return feesInDollars;
      } catch (error) {
        console.error("Error calculating fees:", error);
        // Fallback: return 0 on error
        return 0;
      }
    },
    [event_id, kydEvent?.id, feeCache]
  );

  // Calculate preview data for a given rebate (always percentage)
  const calculatePreviewData = useCallback(
    async (rebate: {
      mode?: "percentage" | "fixed"; // Deprecated: always percentage now
      amount: number;
    }) => {
      if (!event_id || !kydEvent) {
        // Fallback: return empty array if no event_id
        return [];
      }

      return calculatePreviewDataFromAPI(
        rebate,
        actions.ADMIN.calculateFinalTicketPrice,
        event_id
      );
    },
    [event_id, kydEvent?.id]
  );

  // Pre-calculate fees for preview prices when event is available
  useEffect(() => {
    if (!event_id || !kydEvent) {
      // Clear cache if event is not available
      setFeeCache({});
      return;
    }

    let cancelled = false;
    setIsCalculatingFees(true);

    const calculateAllFees = async () => {
      const FACE_VALUES = [10, 20, 50];
      const newCache: Record<number, number> = {};

      for (const faceValue of FACE_VALUES) {
        if (cancelled) return;
        try {
          const { total_fees } = await actions.ADMIN.calculateFinalTicketPrice(
            faceValue,
            event_id
          );
          const feesInDollars = total_fees.getAmount() / 100;
          if (!cancelled) {
            newCache[faceValue] = feesInDollars;
          }
        } catch (error) {
          console.error(`Error calculating fees for $${faceValue}:`, error);
          if (!cancelled) {
            newCache[faceValue] = 0; // Fallback to 0 on error
          }
        }
      }

      if (!cancelled) {
        setFeeCache(newCache);
        setIsCalculatingFees(false);
      }
    };

    calculateAllFees();

    return () => {
      cancelled = true;
    };
  }, [event_id, kydEvent?.id]);

  useEffect(() => {
    if (kydEvent) {
      setBreakeven(
        kydEvent.breakeven ? (kydEvent.breakeven.amount / 100).toString() : "0"
      );
    }
  }, [kydEvent]);

  const onClickArchive = () => {
    open({
      title: `Archive event confirmation "${kydEvent?.name}"`,
      text: `Are you sure you want to archive this event? Event will be moved to draft and not visible to fans.`,
      actions: [
        {
          type: "archive",
          text: "Keep the event",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "archive",
          text: "Archive",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.deleteEvent(kydEvent.id);
              toast({
                status: "success",
                title: "Success!",
                description: "Event archived.",
              });
              await refetch({ silent: true });
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to archive event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onClickCancel = () => {
    open({
      title: `Event cancellation confirmation`,
      text: [
        `By cancelling this event, you will no longer be able to sell tickets and the event will not be visible to fans.`,
      ].join(" "),
      actions: [
        {
          type: "confirm",
          text: "Go Back",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "delete",
          text: "Cancel Event",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.cancelEvent(kydEvent.id);
              await refetch({ silent: true });
              toast({
                status: "success",
                title: "Success!",
                description: `${kydEvent?.name} has been successfully canceled.`,
              });
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to cancel event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onClickDelete = () => {
    open({
      title: `Event deletion confirmation`,
      text: [
        `By deleting this event, you will permanently delete the description, images, ticket types`,
        "promo codes and any specific configurations set.",
      ].join(" "),
      actions: [
        {
          type: "confirm",
          text: "Keep the event",
          buttonProps: {
            variant: "outline",
          },
        },
        {
          type: "delete",
          text: "Permanent delete",
          onClick: async () => {
            setLoading("settings", true);
            close();
            try {
              await actions.ADMIN.deleteEvent(kydEvent.id);
              toast({
                status: "success",
                title: "Success!",
                description: `${kydEvent?.name} has been successfully deleted.`,
              });
              navigate(`/events`);
            } catch (err) {
              toast({
                status: "error",
                title: "Unable to delete event!",
                description: err.message,
              });
            }

            setLoading("settings", false);
          },
          buttonProps: {
            backgroundColor: "kydRed.200",
            color: "white",
            _hover: {
              backgroundColor: "kydRed.200",
            },
          },
        },
      ],
    });
  };

  const onEditBreakeven = async () => {
    setLoading("settings", true);
    close();
    try {
      await actions.ADMIN.updateBreakeven(kydEvent.id, parseInt(breakeven));
      toast({
        status: "success",
        title: "Success!",
        description: "Breakeven updated",
      });
      await refetch({ silent: true });
    } catch (err) {
      toast({
        status: "error",
        title: "Update Failed!",
        description: "Failed to update breakeven amount",
      });
    }

    setIsEditingBreakeven(false);
    setLoading("settings", false);
  };

  const calculatePercentage = (breakeven: number, gmv: number) => {
    if (!breakeven || !gmv) return 0;

    return gmv / breakeven;
  };

  const onClickEditLinkedOrganization = (item: KYDOrganizationEventLink) => {
    setEditingLinkedOrganization(item);
    openLinkOrganizationDialog();
  };

  const onClickDeleteLinkedOrganization = async (
    item: KYDOrganizationEventLink
  ) => {
    dialog.open({
      title: `Stop sharing with ${item.organization.name}`,
      text: `Are you sure you want to stop sharing the event with "${item.organization.name}"?`,
      actions: [
        {
          type: "confirm",
          text: "Delete",
          buttonProps: {
            background: "red",
            color: "white",
          },
          onClick: async () => {
            try {
              setLoading("link_organization", true);
              await actions.ADMIN.disableOrganizationLinkForEvent(
                item.event_id,
                item.organization_id
              );
              await refetch();
              toast({
                duration: 2000,
                status: "success",
                title: "Organization link removed!",
                description: `The link with organization "${item.organization.name}" was deleted. This organization will no longer be able to see your event's data.`,
              });
            } catch (e) {
              toast({
                status: "error",
                title: "Error",
                description: e.message,
              });
            } finally {
              setLoading("link_organization", false);
              dialog.close();
              closeLinkOrganizationDialog();
            }
          },
        },
        {
          type: "cancel",
          text: "Cancel",
        },
      ],
    });
  };

  const onLinkOrganization = async (
    data: Partial<KYDOrganizationEventLink>
  ) => {
    try {
      const isEdit = !!data.created_at;

      setLoading("link_organization", true);
      if (isEdit) {
        await actions.ADMIN.updateLinkedOrganization(kydEvent.id, data);
      } else {
        await actions.ADMIN.linkOrganizationToEvent(kydEvent.id, data);
      }
      await refetch();
      closeLinkOrganizationDialog();
      toast({
        duration: 2000,
        status: "success",
        title: "Success",
        description: `Organization ${
          isEdit ? "permissions updated" : "linked"
        }! Your event will be visible on the "${
          data.organization?.name || data.organization_id
        }" organization ${isEdit ? "with the updated permissions" : ""}.`,
      });
    } catch (e) {
      toast({
        status: "error",
        title: "Failed to link organization",
        description:
          "It was not possible to link the organization. Please contact support",
      });
    } finally {
      setLoading("link_organization", false);
    }
  };

  const progressPercentage = calculatePercentage(
    parseInt(breakeven, 10),
    kydEvent?.total_gmv
  );

  return (
    <Stack maxW="5xl" w="full" gap={4}>
      <LinkOrganizationDialog
        onDelete={onClickDeleteLinkedOrganization}
        onClose={() => {
          setEditingLinkedOrganization(null);
        }}
        kydEvent={kydEvent}
        onSave={onLinkOrganization}
        editingLinkedOrganization={editingLinkedOrganization}
        isLoading={loading.link_organization}
      />
      <Heading w="full" size="md">
        Settings
      </Heading>
      {kydEvent ? (
        <Box
          bg="white"
          border="1px solid #E8E8E8"
          borderRadius="16px"
          p="16px"
          w="full"
          maxW="400px"
        >
          <HStack
            w="full"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <VStack alignItems={"flex-start"} gap={0}>
              <Text fontWeight={"bold"} fontSize="md" color="#1A202C">
                Event Status
              </Text>
              <Text fontSize="sm" color="gray.500">
                {getEventStatusText(kydEvent.status)}
              </Text>
            </VStack>
            {badgeForStatus(kydEvent.status)}
          </HStack>
        </Box>
      ) : null}
      {kydEvent ? (
        <Box
          bg="white"
          border="1px solid #E8E8E8"
          borderRadius="16px"
          p={4}
          w="full"
          maxW="400px"
        >
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <VStack alignItems="flex-start" gap={0}>
              <Text fontWeight="bold" fontSize="md" color="#1A202C">
                Listed
              </Text>
              <Text fontSize="sm" color="gray.500">
                Show this event in your bio and partner feeds
              </Text>
            </VStack>
            <Switch
              aria-label="List event"
              colorScheme="green"
              isChecked={kydEvent.listed ?? true}
              isDisabled={loading.listing}
              onChange={(event) => handleListingChange(event.target.checked)}
            />
          </HStack>
        </Box>
      ) : null}
      {rebateUiEnabled ? (
        <>
          <Divider my={4} />
          <Stack w="full" maxW="600px">
            <Text fontWeight={"bold"} width={"full"}>
              Rebates
            </Text>
            <RebateForm
              previewCardsBgColor={eventRebate ? "gray.100" : "#f5f5f5"}
              rebateTitle={
                eventRebate ? "Custom Event Rebate" : "Global Rebate"
              }
              currentRebate={
                eventRebate
                  ? { amount: Math.round(eventRebate.amount * 100) }
                  : orgRebate
                  ? { amount: Math.round(orgRebate.amount * 100) }
                  : null
              }
              onSave={async (rebate) => {
                setLoading("rebate", true);
                try {
                  const updatedEvent = await actions.ADMIN.updateEventSync(
                    kydEvent.id,
                    "rebate",
                    rebate
                  );
                  showToast(
                    "Rebate override saved",
                    "Event-level rebate override has been set",
                    "success"
                  );
                  await refetch({ silent: true, event: updatedEvent });
                  setIsEditingRebate(false);
                  setIsCreatingRebate(false);
                } catch (err) {
                  showToast("Error", err.message, "error");
                } finally {
                  setLoading("rebate", false);
                }
              }}
              onRemove={eventRebate ? handleRevertToGlobal : undefined}
              onEdit={() => {
                // This is called when canceling from initial edit mode
                setIsEditingRebate(false);
                setIsCreatingRebate(false);
                const rebate = eventRebate || orgRebate;
                setRebateConfig(
                  rebate ? { amount: rebate.amount.toFixed(2) } : { amount: "" }
                );
              }}
              amount={rebateConfig.amount}
              onAmountChange={(amount) =>
                setRebateConfig({ ...rebateConfig, amount })
              }
              isLoading={loading.rebate}
              title={"Event Rebate"}
              description={
                eventRebate
                  ? "Custom rebate set for this event"
                  : "Using global rebate setting"
              }
              banner={
                eventRebate && orgRebate ? (
                  <Box bg="#f5f5f5" borderRadius="md" p={4}>
                    <HStack w="full" justifyContent="space-between">
                      <Text fontWeight={"medium"}>
                        Global Rebate:{" "}
                        <Text as="span" fontWeight={"600"}>
                          {orgRebate?.amount.toFixed(2)}%
                        </Text>
                      </Text>
                      <Text fontWeight={"regular"}>Not applied</Text>
                    </HStack>
                  </Box>
                ) : null
              }
              headerTitle="Event Rebate"
              headerActions={
                <>
                  {eventRebate && !isEditingRebate && !isCreatingRebate && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          setIsEditingRebate(true);
                          setRebateConfig({
                            amount: eventRebate.amount.toFixed(2),
                          });
                        }}
                        isDisabled={loading.rebate}
                        bg="gray.100"
                        border="none"
                        color="gray.700"
                        _hover={{ bg: "gray.200" }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRevertToGlobal}
                        isLoading={loading.rebate}
                        isDisabled={loading.rebate}
                        borderColor="gray.300"
                        color="gray.700"
                        _hover={{ borderColor: "gray.400" }}
                      >
                        Revert to global
                      </Button>
                    </>
                  )}
                  {!eventRebate && !isCreatingRebate && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setIsCreatingRebate(true);
                        if (orgRebate) {
                          setRebateConfig({
                            amount: orgRebate.amount.toFixed(2),
                          });
                        }
                      }}
                      isDisabled={loading.rebate}
                      bg="gray.100"
                      border="none"
                      color="gray.700"
                      _hover={{ bg: "gray.200" }}
                    >
                      Override Rebate
                    </Button>
                  )}
                </>
              }
              calculatePreviewData={calculatePreviewData}
              formatRebateDisplay={formatRebateDisplay}
              initialEditMode={isEditingRebate || isCreatingRebate}
            />
          </Stack>
        </>
      ) : null}
      <Divider my={4} />
      <Stack w="full" maxW={"400px"}>
        <Text fontWeight={"bold"} width={"full"}>
          Breakeven
        </Text>
        <VStack
          w="full"
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
        >
          <Text>GMV: {kydEvent?.total_gmv_display}</Text>
          <Text w="full">Breakeven amount:</Text>
          <HStack>
            <CurrencyInput
              //@ts-ignore
              w="55%"
              maxWidth={"300px"}
              placeholder="$"
              value={breakeven}
              disabled={!isEditingBreakeven}
              isDisabled={!isEditingBreakeven}
              onValueChange={setBreakeven}
              intlConfig={{
                locale: "en-US",
                currency: kydEvent?.currency,
              }}
              customInput={Input}
              step={1}
            />
            {isEditingBreakeven ? (
              <>
                <IconButton
                  icon={<FaCheck />}
                  isDisabled={!breakeven}
                  disabled={!breakeven}
                  aria-label="Confirm edit breakeven"
                  onClick={onEditBreakeven}
                />
                <IconButton
                  icon={<MdCancel />}
                  aria-label="Cancel edit breakeven"
                  onClick={() => {
                    setIsEditingBreakeven(false);
                    setBreakeven("");
                  }}
                />
              </>
            ) : (
              <IconButton
                onClick={() => setIsEditingBreakeven(true)}
                icon={<FaEdit />}
                aria-label="Edit breakeven"
              />
            )}
          </HStack>
          <Progress
            size="md"
            w="full"
            rounded={"lg"}
            colorScheme={"green"}
            value={progressPercentage}
          />
          <Text>{progressPercentage.toFixed(0)}%</Text>
        </VStack>
      </Stack>
      <Divider my={4} />
      <Stack w="full" maxW={"400px"}>
        <Text fontWeight={"bold"} width={"full"}>
          Sales Tax
        </Text>
        {(
          kydEvent?.taxes !== undefined
            ? kydEvent.taxes.length > 0
            : !!kydEvent?.sales_tax
        ) ? (
          <>
            {kydEvent?.taxes !== undefined ? (
              <Table variant="unstyled" size="sm" w="full" ml="-16px">
                <Thead>
                  <Tr>
                    <Th>Type</Th>
                    <Th isNumeric>Rate</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {kydEvent.taxes.map((tax, idx) => (
                    <Tr key={idx}>
                      <Td fontWeight="bold">
                        {tax.type
                          ?.split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </Td>
                      <Td isNumeric>
                        {new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 3,
                        }).format(tax.rate * 100.0)}
                        %
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text>Tax Rate: {kydEvent?.sales_tax.rate * 100.0}%</Text>
            )}
          </>
        ) : (
          <Text>No sales tax for this event.</Text>
        )}
      </Stack>
      <Stack w="full" maxW={"400px"}>
        <Text fontWeight={"bold"} width={"full"}>
          Box Office App
        </Text>
        <VStack
          w="full"
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
        >
          <Link
            isExternal
            href="https://apps.apple.com/us/app/kyd-labs-box-office/id6494989064"
          >
            <Image src={AppStoreBadge} alt="Download on the App Store" />
          </Link>
        </VStack>
      </Stack>
      <Stack w="full">
        <HStack w="full" justifyContent={"space-between"}>
          <Text fontWeight={"bold"} width={"full"}>
            Collaborators
          </Text>

          <IconButton
            variant={"outline"}
            aria-label="Share event with another organization"
            icon={<FaPlus />}
            onClick={() => {
              openLinkOrganizationDialog();
            }}
          />
        </HStack>
        <VStack w="full" overflowX={"scroll"}>
          <LinkedOrganizationsTable
            linked_organizations={kydEvent?.linked_organizations}
            onEdit={onClickEditLinkedOrganization}
            onCreate={openLinkOrganizationDialog}
            isLoading={loading.link_organization}
          />
        </VStack>
      </Stack>
      {kydEvent && kydEvent.display_options?.delete_action?.length > 0 ? (
        <DangerZone
          kydEvent={kydEvent}
          onArchive={onClickArchive}
          onCancel={onClickCancel}
          onDelete={onClickDelete}
          loading={loading.settings}
        />
      ) : null}
    </Stack>
  );
};

export default withConfirmDialog(
  withLinkOrganizationDialog(SettingsNavSection)
);
